import { Tag } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";

import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";

const TAG_GROUPS = [
  {
    key: "clients",
    title: "Клиенты и проектирование",
    names: ["Заказчик", "Дизайнер", "Архитектор"],
  },
  {
    key: "works",
    title: "Работы и подрядчики",
    names: [
      "Подрядчик",
      "Монтаж",
      "Доставка",
      "Замер",
      "Производство",
      "Столяр",
      "Камень",
      "Стекло",
      "Металл",
    ],
  },
  {
    key: "suppliers",
    title: "Поставщики",
    names: ["Поставщик", "Фурнитура", "Материалы", "Оборудование"],
  },
] as const;

const normalizeTagName = (value?: string | null) =>
  (value ?? "").trim().toLocaleLowerCase("ru-RU");

export const ContactListFilter = () => {
  const isMobile = useIsMobile();

  const translate = useTranslate();

  const { data: tags = [] } = useGetList("tags", {
    pagination: {
      page: 1,
      perPage: 100,
    },
    sort: {
      field: "name",
      order: "ASC",
    },
  });

  const groupedTagNames = new Set(
    TAG_GROUPS.flatMap((group) => group.names.map(normalizeTagName)),
  );

  const otherTags = tags.filter(
    (tag) => !groupedTagNames.has(normalizeTagName(tag.name)),
  );

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.contacts.filters.search"),
      }}
    >
      <FilterCategory label="resources.contacts.filters.tags" icon={<Tag />}>
        {TAG_GROUPS.map((group) => {
          const groupTags = tags.filter((tag) =>
            group.names.some(
              (name) => normalizeTagName(name) === normalizeTagName(tag.name),
            ),
          );

          if (groupTags.length === 0) {
            return null;
          }

          return (
            <div key={group.key} className="w-full">
              <div className="px-1 pb-1 pt-2 text-xs font-medium text-muted-foreground first:pt-0">
                {group.title}
              </div>

              <div className="flex flex-col gap-1">
                {groupTags.map((record) => (
                  <ToggleFilterButton
                    key={record.id}
                    className="w-auto md:w-full justify-between h-10 md:h-8"
                    label={
                      <Badge
                        variant="secondary"
                        className="rovero-colored-tag text-[#482719] text-sm md:text-xs font-normal cursor-pointer"
                        style={{
                          backgroundColor: record.color,
                        }}
                      >
                        {record.name}
                      </Badge>
                    }
                    value={{
                      "tags@cs": `{${record.id}}`,
                    }}
                    size={isMobile ? "lg" : undefined}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {otherTags.length > 0 && (
          <div className="w-full">
            <div className="px-1 pb-1 pt-2 text-xs font-medium text-muted-foreground">
              Другие
            </div>

            <div className="flex flex-col gap-1">
              {otherTags.map((record) => (
                <ToggleFilterButton
                  key={record.id}
                  className="w-auto md:w-full justify-between h-10 md:h-8"
                  label={
                    <Badge
                      variant="secondary"
                      className="rovero-colored-tag text-[#482719] text-sm md:text-xs font-normal cursor-pointer"
                      style={{
                        backgroundColor: record.color,
                      }}
                    >
                      {record.name}
                    </Badge>
                  }
                  value={{
                    "tags@cs": `{${record.id}}`,
                  }}
                  size={isMobile ? "lg" : undefined}
                />
              ))}
            </div>
          </div>
        )}
      </FilterCategory>
    </ResponsiveFilters>
  );
};

export { TagFilterSummary as ContactListFilterSummary } from "../misc/TagFilterSummary";
